import View from "@components/View";
import ScalableWrapper from "@components/ScalableWrapper";
import { images } from "@utils/media";
import { LoadingBar } from "./LoadingBar";
import {
  AudioPlaybackCompleteEvent,
  JoinGameEvent,
  PlayersSyncEvent,
  LeaveGameEvent,
  PlayVoiceEvent,
  RemoveBotEvent,
  SendPlayerReadyEvent,
  SetGameConfigEvent,
  WarsEvents,
  SendGameModeEvent
} from "@playroomkit/pr-wars/events";
import { useEffect, useRef, useState } from "react";
import { PlayerData, gameTypeOptions } from "@playroomkit/pr-wars-data";
import { motion, useAnimation } from "framer-motion";
import { VoiceController } from "@playroomkit/pr-wars";
import { HostIds } from "@playroomkit/pr-wars/utils";
import { useUrlMapper } from "@src/contexts/URLMapperContext";
import { useFontSize } from "@hooks/useFontSize";
import { useBots } from "@src/contexts/BotsProvider";
import { GameMode } from "@playroomkit/pr-wars-data";

const spriteConfig = {
  idle: {
    rows: 11,
    columns: 7,
    totalFrames: 75,
  },
  talk: {
    rows: 10,
    columns: 7,
    totalFrames: 67,
  }
}

interface ReadyPlayerState extends PlayerData {
  ready: boolean;
}

export const GameLoadingScreen = ({
  isVisible,
  percentage,
  showLoadingBar,
}: {
  isVisible: boolean;
  percentage: number;
  showLoadingBar: boolean;
}) => {
  const urlMapper = useUrlMapper();
  const controls = useAnimation();
  const { bots } = useBots();

  const [gameMode, setGameMode] = useState<number>(-1);
  const [players, setPlayers] = useState<ReadyPlayerState[]>([]);
  const [WYLDAnimation, setWYLDAnimation] = useState<string>("idle");

  const [subtitle, setSubtitle] = useState<string>("");
  const { containerRef, textRef } = useFontSize(subtitle, 1.1);

  const frameIndexRef = useRef(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const voiceControllerRef = useRef<VoiceController | null>(null);
  const audioEventIdRef = useRef<string | null>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setSubtitle("");
    
    if (!showLoadingBar) {
      voiceControllerRef.current?.teardown();
      voiceControllerRef.current = null;
      // Clear any previous animation
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      // Clear any previous interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      frameIndexRef.current = 0;
    }
  }, [showLoadingBar]);

  useEffect(() => {
    if (!urlMapper || !isVisible || voiceControllerRef.current) return;

    voiceControllerRef.current = new VoiceController(HostIds.WYLD, false, urlMapper);
  }, [urlMapper, isVisible]);
  
  useEffect(() => {
    return () => {
      voiceControllerRef.current?.teardown();
      voiceControllerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const sprite = document.getElementById(WYLDAnimation);
    if (!sprite || !isVisible) return;

    const { columns, totalFrames } = spriteConfig[WYLDAnimation as keyof typeof spriteConfig];

    const baseWidth = 272;
    const baseHeight = 154;

    intervalRef.current = setInterval(() => {
      const frameIndex = frameIndexRef.current;
      const column = frameIndex % columns;
      const row = Math.floor(frameIndex / columns);

      const posX = -column * baseWidth;
      const posY = -row * baseHeight;

      sprite.style.backgroundPosition = `${posX + -18}px ${posY}px`;

      frameIndexRef.current = (frameIndex + 1) % totalFrames;
    }, 1000 / 10);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isVisible, WYLDAnimation]);

  useEffect(() => {
    const handlePlayersSyncEvent = (event: Event) => {
      const e = event as PlayersSyncEvent;
      setPlayers(e.player?.map((player) => ({ ...player, ready: false } as ReadyPlayerState)));
    };

    const handleSendGameModeEvent = (event: Event) => {
      const e = event as SendGameModeEvent;
      setGameMode(e.mode);
    };
    
    const handleJoinGameEvent = (event: Event) => {
      const e = event as JoinGameEvent;
      setPlayers((prev) => {
        const idx = prev.findIndex((p) => p.uid === e.player.uid);
        if (idx !== -1) {
          // Player exists, update their data
          const updated = [...prev];
          updated[idx] = { ...e.player, ready: false } as ReadyPlayerState;
          return updated;
        } else {
          // Player not found, add new
          return [...prev, { ...e.player, ready: false } as ReadyPlayerState];
        }
      });
    };

    const handleLeaveGameEvent = (event: Event) => {
      const e = event as LeaveGameEvent;
      setPlayers((prev) => prev.filter((player) => player.uid !== e.playerId));
    };

    const handleRemoveBotEvent = (event: Event) => {
      const e = event as RemoveBotEvent;
      setPlayers((prev) => prev.filter((player) => player.uid !== e.uid));
    };

    const handleSetGameConfigEvent = (event: Event) => {
      const e = event as SetGameConfigEvent;
      setGameMode(e.mode);
    };

    const handlePlayVoiceEvent = (event: Event) => {
      const e = event as PlayVoiceEvent;
      if (!isVisible || e.entityId !== HostIds.WYLD) return;

        audioEventIdRef.current = e.uid;
        setWYLDAnimation("talk");
        controls.start({
          y: 0,
          transition: {
            type: "spring",
            stiffness: 300,
            damping: 15,
          },
        });
        animateSubtitle(e.text ?? '');
    };

    const animateSubtitle = (text: string) => {
      // Clear any previous animation
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      // Schedule all character updates in parallel
      for (let i = 1; i <= text.length; i++) {
        setTimeout(() => {
          setSubtitle(text.slice(0, i));
        }, 35 * i);
      }
    };

    const handleAudioPlaybackCompleteEvent = (event: Event) => {
      const e = event as AudioPlaybackCompleteEvent;
      if (!isVisible || e.uid !== audioEventIdRef.current) return;

      audioEventIdRef.current = null;
      setWYLDAnimation("idle");
    };

    const handleSendPlayerReadyEvent = (event: Event) => {
      const e = event as SendPlayerReadyEvent;
      setPlayers((prev) => {
        const idx = prev.findIndex((p) => p.uid === e.uid);
        if (idx !== -1) {
          const updated = [...prev];
          updated[idx] = { ...prev[idx], ready: true } as ReadyPlayerState;
          return updated;
        }
        return prev;
      });
    };

    document.addEventListener(WarsEvents.PlayersSyncEvent, handlePlayersSyncEvent);
    document.addEventListener(WarsEvents.JoinGameEvent, handleJoinGameEvent);
    document.addEventListener(WarsEvents.LeaveGameEvent, handleLeaveGameEvent);
    document.addEventListener(WarsEvents.RemoveBotEvent, handleRemoveBotEvent);
    document.addEventListener(WarsEvents.SetGameConfigEvent, handleSetGameConfigEvent);
    document.addEventListener(WarsEvents.PlayVoiceEvent, handlePlayVoiceEvent);
    document.addEventListener(WarsEvents.AudioPlaybackCompleteEvent, handleAudioPlaybackCompleteEvent);
    document.addEventListener(WarsEvents.SendPlayerReadyEvent, handleSendPlayerReadyEvent);
    document.addEventListener(WarsEvents.SendGameModeEvent, handleSendGameModeEvent);

    return () => {
      document.removeEventListener(WarsEvents.JoinGameEvent, handleJoinGameEvent);
      document.removeEventListener(WarsEvents.LeaveGameEvent, handleLeaveGameEvent);
      document.removeEventListener(WarsEvents.RemoveBotEvent, handleRemoveBotEvent);
      document.removeEventListener(WarsEvents.SetGameConfigEvent, handleSetGameConfigEvent);
      document.removeEventListener(WarsEvents.PlayVoiceEvent, handlePlayVoiceEvent);
      document.removeEventListener(WarsEvents.AudioPlaybackCompleteEvent, handleAudioPlaybackCompleteEvent);
      document.removeEventListener(WarsEvents.SendPlayerReadyEvent, handleSendPlayerReadyEvent);
      document.removeEventListener(WarsEvents.PlayersSyncEvent, handlePlayersSyncEvent);
      document.removeEventListener(WarsEvents.SendGameModeEvent, handleSendGameModeEvent);
    };
  }, [isVisible, showLoadingBar]);

  return (
    <View
      className={`absolute top-0 left-0 w-screen h-d-screen overflow-hidden ${gameMode === GameMode.ELIMINATION ? "bg-elimination-mode-bg" : "bg-tally-mode-bg"} ${
        isVisible ? "opacity-100" : "opacity-0"
      }`}
      enableEventCapturing
    >
      <ScalableWrapper anchor="top-left" maintainAspectRatioBy="height">
        <img
          src={images.GameLogo}
          alt="Loading Background"
          className="w-64 h-auto object-contain p-4"
        />
      </ScalableWrapper>
      <ScalableWrapper anchor="top-right" maintainAspectRatioBy="height">
        <View className="w-72 absolute top-8 right-8 text-center font-[Slackey] flex flex-col items-center">
          <h4
            className="text-white text-2xl leading-none my-0"
            style={{
              textShadow: `-3px -3px 0 #000, 3px -3px 0 #000, -3px 3px 0 #000, 3px 3px 0 #000, 0px 3px 0 #000, 3px 0px 0 #000, 0px -3px 0 #000, -3px 0px 0 #000, 3px 3px 0 #000`,
            }}
          >
            Mode
          </h4>
          <h1
            className="text-white text-4xl leading-none my-3"
            style={{
              textShadow: `-4px -4px 0 #000, 4px -4px 0 #000, -4px 4px 0 #000, 4px 4px 0 #000, 0px 4px 0 #000, 4px 0px 0 #000, 0px -4px 0 #000, -4px 0px 0 #000, 4px 4px 0 #000`,
            }}
          >
            {
              gameTypeOptions.find(
                (option) => option.id === gameMode
              )?.name
            }
          </h1>
          <View className="w-56">
            <LoadingBar
              text=""
              percentage={percentage}
              color="white"
            />
          </View>
        </View>
      </ScalableWrapper>
      <ScalableWrapper maintainAspectRatioBy="height">
        <motion.div
          animate={controls}
          initial={{ y: -600 }}
          className="absolute -top-56 -left-10 w-full h-full flex items-center justify-center scale-90"
        >
          <View className="absolute top-[25rem] left-[35.25rem] flex justify-center w-[15rem] h-[154px] bg-no-repeat overflow-hidden scale-[2] rounded-3xl">
            <View
              id="idle"
              className={`absolute top-0 left-0 w-[17rem] h-[154px] bg-no-repeat bg-center ${WYLDAnimation === "idle" ? "z-10" : "-z-1"}`}
              style={{ backgroundImage: `url(${images.WYLDIdle})` }}
            />
            <View
              id="talk"
              className={`absolute top-0 left-0 w-[17rem] h-[154px] bg-no-repeat bg-center ${WYLDAnimation === "talk" ? "z-10" : "-z-1"}`}
              style={{ backgroundImage: `url(${images.WYLDTalk})` }}
            />
          </View>
          <img
            src={images.WYLDScreen}
            alt="WYLDScreen"
            className="absolute w-[60rem] h-auto object-contain z-10"
          />
        </motion.div>
        <View className={`absolute bottom-0 left-0 w-full h-full flex items-center justify-center ${subtitle ? "opacity-100" : "opacity-0"}`}>
          <View ref={containerRef} className="w-[36rem] h-[5.5rem] bg-black/50 rounded-xl translate-y-32 px-6 py-1">
            <h1 ref={textRef} className="text-white text-lg my-3 font-mono">{subtitle}</h1>
          </View>
        </View>
      </ScalableWrapper>
      <ScalableWrapper anchor="bottom" maintainAspectRatioBy="height">
        <View className="absolute bottom-0 w-full flex items-start justify-center pb-2 gap-4">
          {players.map((player) => {
            const isBot = bots.some((bot) => bot.id === player.uid);
            return <View
            key={player.uid}
            className="relative w-30 font-[Slackey] flex flex-col items-center gap-4"
          >
            <img
              src={urlMapper.mapUrl(player.profileImageUrl ?? "")}
              alt="player-avatar"
              className="w-16 h-auto object-contain rounded-full outline outline-4 outline-white"
              style={{
                boxShadow: "2px 2px 6px 1px black",
              }}
            />
           {(player.ready || isBot) && <View className="absolute bottom-10 w-4 h-4 bg-white p-2 flex items-center justify-center rounded-full outline outline-2 outline-black">
              <img src={images.BlackTickIcon} className="w-3 h-3 object-contain" />
            </View>
            }
            <h1
              className="w-24 h-8 text-center text-[#FFE98A] text-xs leading-none my-0 truncate"
              style={{ whiteSpace: "wrap" }}
            >
              {player.displayName}
            </h1>
          </View>
          }
            
          )}
        </View>
      </ScalableWrapper>
    </View>
  );
};
